// The free trading report card at /report-card: a visitor drops in a trade file and sees an
// honest grade on it right away, without an account. Everything runs in the browser; the
// file is only sent anywhere if they sign up and import it (see PENDING_IMPORT_KEY).
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { PublicShell, usePageLanguage, useTitle } from './PublicPage';
import { parseTradesCsv } from './csv';
import { buildInsights } from './insights';
import { performanceStats } from './stats';
import { formatMoney, tradingDayKey } from './pnl';
import { currentLocale } from './langState';
import ReportCard from './ReportCard';
import { TRADE_ONLY_SUBJECTS, addDays } from './weeklyGrades';
import { trackReport } from './analytics';
import { useI18n } from './i18n';
import { api } from './api';
import { missingFees } from './MissingFees';
import PlatformRequest from './PlatformRequest';
import { PRESETS } from './balance';
import { FirmSearch, PlanPicker, firmPlans } from './FirmPicker';
import { ruleCheck } from './ruleCheck';
import { EquityChart, HourChart } from './ReportCharts';
import GradeShare from './GradeShare';
import { EXPORTS, STOCK_EXPORTS } from './exportSteps';

// The file waiting to be imported once the visitor has an account (read by Journal.js).
export const PENDING_IMPORT_KEY = 'mm-pending-import';
const SAMPLE_URL = '/samples/report-card-sample.csv';
const SAMPLE_NAME = 'report-card-sample.csv';
// sessionStorage holds a few MB; bigger files are simply imported by hand after sign-up.
const MAX_STASH = 3 * 1024 * 1024;

// The free report covers the most recent 2 weeks of the file; the rest needs an account.
const FREE_DAYS = 14;

// The trades in the 14 days ending on the file's last trading day, and that range.
function lastMonth(trades) {
  const days = trades.map((t) => tradingDayKey(t.date)).sort();
  const last = days[days.length - 1];
  const from = addDays(last, -(FREE_DAYS - 1));
  return { trades: trades.filter((t) => tradingDayKey(t.date) >= from), from, to: last, firstDay: days[0] };
}

const mask = (number) => (number.length > 4 ? `…${number.slice(-4)}` : number);

function analyse(text, fileName) {
  const parsed = parseTradesCsv(text);
  // Trades from the file are grouped by their account number, when the file has one.
  const numbers = [...new Set(parsed.trades.map((t) => t.accountNumber).filter(Boolean))];
  const trades = parsed.trades.map((t) => ({ ...t, accountId: t.accountNumber || 'file' }));
  return { fileName, text, parsed, trades, numbers };
}

// A plain verdict from the overall numbers, so the honest take never comes up empty: what win
// rate the trader's average win and loss need to break even, against the one they have.
function verdict(stats, t) {
  if (!stats.count || stats.avgWin == null || stats.avgLoss == null) return null;
  const W = stats.avgWin;
  const L = stats.avgLoss;
  const be = (L / (W + L)) * 100;
  const wr = stats.winRate || 0;
  const money = (n) => formatMoney(n).replace(/^\+/, '');
  const text = L > W
    ? t('Your average loss ({loss}) is {ratio}× your average win ({win}), so you need to win {be}% of your trades just to break even. You win {wr}%.',
      { loss: money(-L), win: money(W), ratio: (L / W).toFixed(1), be: Math.round(be), wr: Math.round(wr) })
    : t('Your average win ({win}) is bigger than your average loss ({loss}), so you only need to win {be}% of your trades to break even. You win {wr}%.',
      { loss: money(-L), win: money(W), be: Math.round(be), wr: Math.round(wr) });
  const tip = wr >= be + 5
    ? t('The math is on your side. Your job is to keep your losses this size and stop giving it back on bad days.')
    : wr >= be
      ? t('You’re barely above break-even. One habit, like a moved stop or a revenge trade, is the difference between green and red.')
      : L > W
        ? t('At this win rate your losses are too big. Cut every loss to under {target} or this keeps bleeding.', { target: money(W) })
        : t('Your wins are big enough, but you lose too often. Take fewer trades, only your best setup.');
  return { area: t('The numbers'), text, tip, sample: stats.count, small: stats.count < 15 };
}

// The findings' advice reads "Honest take: …"; on this page it sits under a "Do this" label.
const stripLead = (tip) => String(tip || '').replace(/^(Honest take|Opinión honesta):\s*/, '').replace(/^./, (c) => c.toUpperCase());

function EarlyTag({ f }) {
  const { t } = useI18n();
  if (!f.small) return null;
  const text = f.days ? t('Early signal · {n} days', { n: f.days }) : f.sample ? t('Early signal · {n} trades', { n: f.sample }) : t('Early signal');
  return <span className="finding-small" title={t('Based on only a few trades or days, so it’s still early. Watch it, don’t treat it as a rule yet.')}>{text}</span>;
}

// What was read from the file, and anything that could make the numbers wrong.
function FileCheck({ parsed, trades, range }) {
  const { t } = useI18n();
  const f = parsed.format || {};
  const noFees = missingFees(trades).length;
  const items = [
    { ok: true, text: t('{n} trades read, {from} – {to}', { n: trades.length, from: range.from, to: range.to }) },
    noFees && { ok: false, text: t('{n} futures trades have no fees in the file, so your net P&L may look better than it really is.', { n: noFees }) },
    parsed.errors.length > 0 && { ok: false, text: t('{n} rows couldn’t be read and were left out.', { n: parsed.errors.length }) },
    f.unmatched > 0 && { ok: false, text: t('{n} sales of positions opened before this file starts were left out.', { n: f.unmatched }) },
    f.stillOpen > 0 && { ok: false, text: t('{n} positions were still open at the end of the file and aren’t counted yet.', { n: f.stillOpen }) },
    f.timeUnknown && { ok: false, text: t('This file has dates but no times, so there’s no time-of-day breakdown.') },
  ].filter(Boolean);
  return (
    <ul className="free-file-check" aria-label={t('File check')}>
      {items.map((x) => <li key={x.text} className={x.ok ? 'ok' : 'warn'}><span aria-hidden="true">{x.ok ? '✓' : '!'}</span> {x.text}</li>)}
    </ul>
  );
}

function ExportSteps() {
  const { t, tr } = useI18n();
  return (
    <details className="free-export">
      <summary>{t('How to export your trades')}</summary>
      <div className="export-guides">
        {[...EXPORTS, ...STOCK_EXPORTS].map((x) => (
          <div key={x.name} className="export-guide">
            <h3>{t(x.name)}</h3>
            <ol>{x.steps.map((step) => <li key={step}>{t(step)}</li>)}</ol>
          </div>
        ))}
      </div>
      <p className="export-note">{tr('Menus can change when platforms update. Stuck? <0>Send us a message</0> and we’ll help you find it.', [(x) => <Link to="/contact">{x}</Link>])}</p>
    </details>
  );
}

// "Would a prop account have survived these trades?" for a chosen firm preset.
function PropCheck({ trades, onSave, isSample }) {
  const { t } = useI18n();
  const [id, setId] = useState('topstep-combine-50k');
  const preset = PRESETS.find((p) => p.id === id);
  const res = preset ? ruleCheck(trades, preset) : null;
  const day = (key) => new Date(`${key}T12:00:00`).toLocaleDateString(currentLocale(), { weekday: 'short', month: 'short', day: 'numeric' });
  const money = (n) => formatMoney(n).replace(/^\+/, '');
  const lines = !res ? [] : [
    res.blownOn
      ? { bad: true, text: t('It would have hit the {limit} max loss on {day}. The account would have failed there.', { limit: money(preset.maxLossLimit), day: day(res.blownOn) }) }
      : preset.maxLossLimit && { bad: false, text: t('Never hit the max loss. {room} of room left at the end.', { room: money(res.toBlow) }) },
    preset.dailyLossLimit && (res.dailyHits.length
      ? { bad: true, text: t('The {limit} daily loss limit would have been hit on {days}.', { limit: money(preset.dailyLossLimit), days: res.dailyHits.map((d) => day(d.day)).join(', ') }) }
      : { bad: false, text: t('Never hit the {limit} daily loss limit.', { limit: money(preset.dailyLossLimit) }) }),
    preset.profitTarget && (res.targetOn
      ? { bad: false, text: t('Profit target of {target} reached on {day}.', { target: money(preset.profitTarget), day: day(res.targetOn) }) }
      : { bad: res.change < 0, text: t('{change} so far: {left} short of the {target} profit target.', { change: formatMoney(res.change), left: money(preset.profitTarget - res.change), target: money(preset.profitTarget) }) }),
    preset.consistencyLimit && res.consistencyPct !== null && {
      bad: !res.consistencyOk,
      text: t('Best day is {pct}% of your profit; the consistency limit is {limit}%.', { pct: Math.round(res.consistencyPct), limit: preset.consistencyLimit }),
    },
  ].filter(Boolean);
  const [firm, setFirm] = useState(preset ? preset.firm : '');
  return (
    <section className="insight-panel free-prop">
      <h2>{t('Would your prop account have survived these trades?')}</h2>
      <div className="free-prop-pick">
        <span>{t('Firm and account')}</span>
        <FirmSearch id="free-firm" value={firm} allowNone={false} onChange={(f) => { setFirm(f); const first = firmPlans(f)[0]; const p50 = first && (first.presets.find((p) => p.size === 50000) || first.presets[0]); if (p50) setId(p50.id); }} />
        {firm && <PlanPicker key={firm} firm={firm} presetId={id} onPick={(p) => setId(p.id)} />}
      </div>
      <ul className="free-prop-lines">
        {lines.map((l) => <li key={l.text} className={l.bad ? 'bad' : 'good'}><span aria-hidden="true">{l.bad ? '✕' : '✓'}</span> {l.text}</li>)}
      </ul>
      <p className="free-report-note">{t('A replay of these trades under the firm’s published rules (checked October 2026), starting from a fresh account. Firms change their rules, so check yours.')}</p>
      {!isSample && <button type="button" className="secondary small" onClick={onSave}>{t('Track these rules live in my journal')} &rarr;</button>}
    </section>
  );
}

// "Email me every Monday": a lighter step than an account.
function ReminderForm() {
  const { t, lang } = useI18n();
  const [email, setEmail] = useState('');
  const [state, setState] = useState('idle');
  const [error, setError] = useState('');
  async function submit(e) {
    e.preventDefault();
    setState('busy');
    setError('');
    try {
      await api('/guide/report-reminder', { method: 'POST', body: { email, lang, company: e.target.company.value } });
      setState('done');
      trackReport('reportReminder');
    } catch (err) {
      setError(err.message);
      setState('idle');
    }
  }
  if (state === 'done') return <p className="free-reminder done">{t('You’re set. Check your inbox: we’ll remind you every Monday morning to grade your week.')}</p>;
  return (
    <form className="free-reminder" onSubmit={submit}>
      <span>{t('Not ready for an account? Get a reminder every Monday to grade your week.')}</span>
      <div className="free-reminder-row">
        <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t('you@example.com')} aria-label={t('Your email')} />
        <input type="text" name="company" tabIndex={-1} autoComplete="off" className="visually-hidden" aria-hidden="true" />
        <button type="submit" className="secondary small" disabled={state === 'busy'}>{state === 'busy' ? t('Saving…') : t('Remind me on Mondays')}</button>
      </div>
      {error && <p className="error">{error}</p>}
      <small>{t('One email a week. Unsubscribe anytime.')}</small>
    </form>
  );
}

export default function FreeReportCard({ user, forceLang }) {
  const lang = usePageLanguage(forceLang, '/report-card');
  const { t } = useI18n();
  useTitle(t('Free Trading Report Card: an Honest Grade on Your Trades | Market Matrix'));
  const navigate = useNavigate();
  const input = useRef(null);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  // A file we couldn't read, so the visitor can send it to us (PlatformRequest).
  const [badFile, setBadFile] = useState(null);

  const result = useMemo(() => {
    if (!data || !data.trades.length) return null;
    const month = lastMonth(data.trades);
    // Named here (not when the file is read) so they follow the page language.
    const accounts = data.numbers.map((n) => ({ _id: n, name: `${t('Account')} ${mask(n)}` }));
    const insights = buildInsights({ trades: month.trades, notes: {}, rules: [], library: [], accounts });
    return { insights, stats: performanceStats(month.trades), month, hidden: data.trades.length - month.trades.length };
  }, [data, lang]); // eslint-disable-line react-hooks/exhaustive-deps

  // `auto` is the sample shown when the page opens: no tracking, no scrolling.
  function load(text, fileName, sample, auto = false) {
    const next = analyse(text, fileName);
    if (!next.trades.length) {
      setError(t('We couldn’t find any trades in that file. Try your platform’s trade or order history export, or a CSV with symbol, entry and exit prices.'));
      return false;
    }
    setData(next);
    setError('');
    if (auto) return;
    trackReport(sample ? 'reportSample' : 'reportUpload');
    setTimeout(() => document.getElementById('your-report')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
  }

  // The sample report is open from the start, so visitors see what they'll get.
  useEffect(() => {
    let live = true;
    fetch(SAMPLE_URL)
      .then((res) => res.text())
      .then((text) => {
        if (live) setData((current) => current || analyse(text, SAMPLE_NAME));
      })
      .catch(() => {});
    return () => {
      live = false;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function onFile(e) {
    const file = e.target.files[0];
    if (!file) return;
    setLoading(true);
    setBadFile(null);
    try {
      if (load(await file.text(), file.name, false) === false) setBadFile(file);
    } catch {
      setError(t('That file couldn’t be read. Please choose a CSV file.'));
      setBadFile(file);
    } finally {
      setLoading(false);
    }
  }

  async function trySample() {
    setLoading(true);
    try {
      const res = await fetch(SAMPLE_URL);
      load(await res.text(), SAMPLE_NAME, true);
    } catch {
      setError(t('The sample file didn’t load. Please try again.'));
    } finally {
      setLoading(false);
    }
  }

  // Keeps the file for the import screen, then signs up (or opens the journal).
  function save() {
    trackReport('reportSignup');
    const isSample = data.fileName === SAMPLE_NAME;
    if (!isSample && data.text.length <= MAX_STASH) {
      try {
        sessionStorage.setItem(PENDING_IMPORT_KEY, JSON.stringify({ text: data.text, fileName: data.fileName }));
      } catch {
        // Too big or storage blocked: they can import the file after signing up.
      }
    }
    navigate(user && !user.isDemo ? '/app' : '/signup');
  }

  const s = result && result.stats;
  const r = result && result.insights;
  // The honest take always says something: the comparisons when there are any, else the
  // break-even verdict plus the biggest leak.
  const leakPromoted = Boolean(r && !r.honest.length && r.costing.length);
  const costingShown = r ? r.costing.slice(leakPromoted ? 1 : 0, leakPromoted ? 4 : 3) : [];
  const honest = !r ? [] : r.honest.length ? r.honest
    : [r.costing[0] && { ...r.costing[0], tip: t('This is your biggest leak. Fix this one thing before anything else.') }, verdict(s, t)].filter(Boolean);
  // The biggest one leads the page as "Fix this first"; the rest follow once each.
  const [fixFirst, ...moreHonest] = honest;
  const honestAreas = new Set(honest.map((f) => f.area));
  const workingShown = r ? r.working.filter((f) => !honestAreas.has(f.area)).slice(0, 3) : [];
  const isSample = data && data.fileName === SAMPLE_NAME;
  const dayLabel = (key) => new Date(`${key}T12:00:00`).toLocaleDateString(currentLocale(), { month: 'short', day: 'numeric', year: 'numeric' });
  const chooseFile = () => input.current?.click();

  return (
    <PublicShell user={user} className="firm-page template-page free-report">
      <header className="page-title firm-hero">
        <p className="eyebrow">{t('Free tool · no sign-up')}</p>
        <h1>{t('Free trading report card')}</h1>
        <p className="subtitle">{t('Your P&L tells you what happened. Your report card tells you why.')}</p>
        <p className="free-report-lead">{t('Drop in your trade file and get an honest grade on your trading in seconds: what’s working, what’s costing you, and the one thing to fix first.')}</p>
        <div className="firm-cta">
          <button type="button" className="cta-button" onClick={() => input.current?.click()} disabled={loading}>
            {loading ? t('Reading…') : t('Choose my trade file (CSV)')}
          </button>
          {data && !isSample && (
            <button type="button" className="firm-secondary link-button" onClick={trySample} disabled={loading}>{t('Show the sample report again')}</button>
          )}
          <input ref={input} type="file" accept=".csv,text/csv,.txt,.tsv,.xls,.xlsx,.pdf" onChange={onFile} hidden />
        </div>
        <p className="free-privacy"><span aria-hidden="true">🔒</span> {t('Your file stays in your browser. Nothing is uploaded unless you create an account, or choose to send us a file we can’t read yet.')}</p>
        <p className="free-report-formats">
          {t('Works with TopstepX, Tradovate and NinjaTrader web exports, Webull, Schwab and Robinhood history, or any CSV with symbol, entry and exit prices.')}
        </p>
        <ExportSteps />
        {error && <p className="error">{error}</p>}
        {badFile && <PlatformRequest key={badFile.name + badFile.size} file={badFile} user={user} from="report-card" />}
      </header>

      {result && (
        <div id="your-report" className="free-report-result">
          {isSample && (
            <div className="free-report-sample-bar">
              <span><strong>{t('This is a sample report')}</strong> {t('(made-up trades from two prop accounts).')} {t('Want yours?')}</span>
              <button type="button" className="small" onClick={chooseFile} disabled={loading}>{t('Choose my trade file')}</button>
            </div>
          )}
          <section className="firm-section">
            <p className="eyebrow">{isSample ? t('Sample report') : t('Your report')} &middot; {data.fileName}</p>
            <h2>{t('{n} trades · {net}', { n: s.count, net: formatMoney(s.net) })}</h2>
            <p className="free-report-stats">
              {t('Win rate {rate}% · profit factor {pf} · average win {win} · average loss {loss}', {
                rate: Math.round(s.winRate || 0),
                pf: s.profitFactor === Infinity ? '∞' : s.profitFactor == null ? '—' : s.profitFactor.toFixed(2),
                win: s.avgWin == null ? '—' : formatMoney(s.avgWin),
                loss: s.avgLoss == null ? '—' : formatMoney(-s.avgLoss),
              })}
            </p>
          </section>

          <FileCheck parsed={data.parsed} trades={data.trades} range={{ from: dayLabel(result.month.firstDay), to: dayLabel(result.month.to) }} />

          {result.hidden > 0 && (
            <div className="free-report-capped">
              <p>
                {t('Your file has {total} trades going back to {first}. Without an account, the free report covers the most recent 2 weeks: {from} – {to} ({n} trades). Create a free account for your full report, and we’ll keep your trades and track your progress week after week.', {
                  total: data.trades.length, first: dayLabel(result.month.firstDay), from: dayLabel(result.month.from), to: dayLabel(result.month.to), n: s.count,
                })}
              </p>
              {!isSample && <button type="button" className="small" onClick={save}>{t('Get my full report free')} &rarr;</button>}
            </div>
          )}
          {s.count < 30 && (
            <p className="free-report-small">{t('Based on only {n} trades. Add a month or more of history for a fuller report.', { n: s.count })}</p>
          )}

          {fixFirst && (
            <section className="free-fix" aria-labelledby="free-fix-title">
              <span className="free-fix-label" id="free-fix-title">{t('Fix this first')}</span>
              <span className="finding-area">{fixFirst.area}<EarlyTag f={fixFirst} /></span>
              <p className="free-fix-text">{fixFirst.text}</p>
              <p className="free-fix-do"><strong>{t('Do this:')}</strong> {stripLead(fixFirst.tip)}</p>
            </section>
          )}

          {moreHonest.length > 0 && (
            <section className="honest-take" aria-labelledby="free-honest-title">
              <h2 id="free-honest-title">{t('Also worth hearing')}</h2>
              <ul>
                {moreHonest.map((f) => (
                  <li key={f.area}>
                    <span className="finding-area">{f.area}<EarlyTag f={f} /></span>
                    <p>{f.text}</p>
                    <p className="honest-take-tip"><strong>{t('Do this:')}</strong> {stripLead(f.tip)}</p>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {(costingShown.length > 0 || workingShown.length > 0) && (
            <div className="free-report-columns">
              <section className="insight-panel free-cost">
                <h2>{t("What's costing you")}</h2>
                {costingShown.length ? (
                  <ul className="compare-findings">
                    {costingShown.map((f) => (
                      <li key={f.text}><span className="finding-area">{f.area}<EarlyTag f={f} /></span>{f.text}</li>
                    ))}
                  </ul>
                ) : <p className="empty">{t('No other losing patterns stand out in this file.')}</p>}
              </section>
              <section className="insight-panel free-work">
                <h2>{t("What's working")}</h2>
                {workingShown.length ? (
                  <ul className="compare-findings">
                    {workingShown.map((f) => (
                      <li key={f.text}><span className="finding-area">{f.area}<EarlyTag f={f} /></span>{f.text}</li>
                    ))}
                  </ul>
                ) : <p className="empty">{t('Nothing stands out yet. Keep trading your plan.')}</p>}
              </section>
            </div>
          )}

          <div className="free-report-columns">
            <EquityChart trades={result.month.trades} />
            <HourChart trades={result.month.trades} />
          </div>

          <PropCheck trades={result.month.trades} onSave={save} isSample={isSample} />

          <ReportCard key={`${data.fileName}-${data.trades.length}`} improveLinks="tools" trades={result.month.trades} onEarlier={result.hidden > 0 && !isSample ? save : undefined}
            notes={{}} rules={[]} library={[]} subjects={TRADE_ONLY_SUBJECTS} noJournal adminPreview={false}
            extraSubjects={['Discipline', 'Journaling'].map((label) => (
              <li key={label} className="report-subject report-locked-subject">
                <div className="report-subject-head">
                  <span className="report-grade">🔒</span>
                  <div><strong>{t(label)}</strong></div>
                </div>
                <p className="report-comment">{label === 'Discipline'
                  ? t('Graded from your own rules and the trades you tag as mistakes.')
                  : t('Graded from your setup tags, morning prep and end-of-day reflections.')}</p>
                <button type="button" className="link-button" onClick={isSample ? chooseFile : save}>{t('Unlock with a free journal')} &rarr;</button>
              </li>
            ))}
            onMonthLocked={isSample ? chooseFile : save}
            renderFooter={(card, week) => <GradeShare card={card} week={week} />} />

          <section className="final-cta blog-final-cta free-report-save">
            <h2>{isSample ? t('Now see your own trades') : t('Keep tracking your progress, week after week')}</h2>
            <p>{isSample
              ? t('Choose your trade file and get this same report on your own trading, in seconds. Your file stays in your browser.')
              : t('Create a free account and these trades go straight into your journal. Every week you get a new report card that shows whether you’re improving: which grades went up, which went down, and what to work on next. Plus your prop firm’s rules tracked live, morning prep and end-of-day reflection.')}</p>
            {isSample ? (
              <button type="button" className="cta-button" onClick={chooseFile} disabled={loading}>{t('Choose my trade file (CSV)')} &rarr;</button>
            ) : (
              <button type="button" className="cta-button" onClick={save}>
                {user && !user.isDemo ? t('Import these trades into my journal') : t('Create my free account and track my progress')} &rarr;
              </button>
            )}
            <span className="cta-note">{t('Every feature free for 14 days, no credit card. The core journal stays free.')}</span>
            {!isSample && !(user && !user.isDemo) && <ReminderForm />}
          </section>
        </div>
      )}

      {!result && (
        <section className="firm-section">
          <h2>{t('What you get')}</h2>
          <ul className="firm-watch">
            <li><strong>{t('An honest take.')}</strong> {t('Which markets and accounts actually pay you, which ones don’t, and whether you switch after losses.')}</li>
            <li><strong>{t('A weekly report card.')}</strong> {t('Your latest week graded A+ to F on results, risk control, emotional control and focus, compared with the week before.')}</li>
            <li><strong>{t('What’s costing you.')}</strong> {t('The times, setups and habits that lose money, in dollars, straight from your trades.')}</li>
            <li><strong>{t('Your assignments for next week.')}</strong> {t('Concrete things to change, taken from your weakest grades.')}</li>
          </ul>
        </section>
      )}

      <section className="firm-section">
        <p className="firm-note">{t('For journaling and education only, not financial advice. Grades come only from the trades in your file. Trading futures involves substantial risk of loss.')}</p>
      </section>
    </PublicShell>
  );
}
